import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { GraduationCap, ArrowRight, CalendarCheck, Send, X, Clock, Layers, Award } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHero, Eyebrow, GoldButton, PrimaryButton } from "@/components/Ui";
import { POLI } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Formazione = () => {
  const [courses, setCourses] = useState([]);
  const [cat, setCat] = useState("Tutte");
  const [modal, setModal] = useState(null); // course object for info request
  const [form, setForm] = useState({ nome: "", cognome: "", telefono: "", email: "", messaggio: "" });
  const [sending, setSending] = useState(false);
  const [logoErr, setLogoErr] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { axios.get(`${API}/courses`).then((r) => setCourses(r.data)); }, []);

  const cats = ["Tutte", ...new Set(courses.map((c) => c.categoria))];
  const filtered = cat === "Tutte" ? courses : courses.filter((c) => c.categoria === cat);

  const openInfo = (c) => { setModal(c); setForm({ nome: "", cognome: "", telefono: "", email: "", messaggio: "" }); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submitInfo = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.telefono || !form.email) { toast.error("Compila i campi obbligatori."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/info-requests`, { ...form, corso: modal?.titolo || "" });
      toast.success("Richiesta inviata! Ti contatteremo a breve."); setModal(null);
    } catch { toast.error("Errore nell'invio. Riprova."); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="formazione-page">
      <PageHero eyebrow="Formazione · Università eCampus" title="Percorsi formativi e universitari" subtitle="L'offerta formativa in collaborazione con l'Università eCampus e i poli didattici: corsi, percorsi e certificazioni per la tua carriera." />

      {/* eCampus intro */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Eyebrow>Università eCampus</Eyebrow>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl font-medium text-navy-900">Un'offerta formativa completa</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">L'area Formazione raccoglie i percorsi accademici e professionali disponibili attraverso l'Università eCampus e i nostri poli didattici. Consulta le schede dei corsi, richiedi informazioni o prenota una consulenza dedicata: ti guideremo nella scelta del percorso più adatto ai tuoi obiettivi.</p>
            <div className="mt-8 flex flex-wrap gap-4"><PrimaryButton href="https://book.heygoldie.com/PalumboAcademy">Prenota una consulenza <ArrowRight className="w-4 h-4" /></PrimaryButton></div>
          </div>
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 shadow-crisp p-6 flex items-center justify-center min-h-[160px]" data-testid="ecampus-logo-slot">
              {logoErr ? (
                <div className="text-center">
                  <GraduationCap className="w-10 h-10 text-gold-500 mx-auto" />
                  <p className="mt-3 font-serif text-lg font-semibold text-navy-900">Logo Università eCampus</p>
                  <p className="mt-1 text-xs text-slate-500 max-w-[220px]">Carica il file del logo in <code>/public/ecampus-logo.png</code> per visualizzarlo qui.</p>
                </div>
              ) : (
                <img src={`${process.env.PUBLIC_URL}/ecampus-logo.png`} alt="Università eCampus" onError={() => setLogoErr(true)} className="max-h-28 w-auto object-contain" data-testid="ecampus-logo-img" />
              )}
            </div>
            <div className="bg-navy-900 text-white p-8">
              <h3 className="font-serif text-2xl font-semibold">Poli e certificazioni</h3>
              <ul className="mt-4 space-y-2 text-sm text-white/80">{POLI.map((p) => <li key={p.name} className="flex items-center gap-2"><Award className="w-4 h-4 text-gold-500" /> {p.name}</li>)}</ul>
            </div>
          </div>
        </div>
      </section>

      {/* Courses */}
      <section className="bg-[#F8F9FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl"><Eyebrow>Corsi e percorsi</Eyebrow><h2 className="mt-3 font-serif text-4xl md:text-5xl font-medium text-navy-900">Scopri l'offerta</h2></div>
          {courses.length > 1 && (
            <div className="mt-8 flex flex-wrap gap-2" data-testid="course-categories">
              {cats.map((c) => <button key={c} onClick={() => setCat(c)} data-testid={`course-cat-${c}`} className={`px-4 py-2 text-xs uppercase tracking-[0.1em] font-bold border transition-colors ${cat === c ? "bg-navy-900 text-white border-navy-900" : "border-gray-200 text-navy-900 hover:border-gold-500"}`}>{c}</button>)}
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="mt-10 bg-white border border-dashed border-gray-300 p-12 text-center" data-testid="courses-empty">
              <GraduationCap className="w-12 h-12 text-gold-500 mx-auto" />
              <p className="mt-4 font-serif text-2xl text-navy-900">Corsi in arrivo</p>
              <p className="mt-2 text-slate-600">I percorsi formativi saranno pubblicati a breve. Contattaci per informazioni.</p>
              <div className="mt-6"><GoldButton href="https://book.heygoldie.com/PalumboAcademy"><CalendarCheck className="w-4 h-4" /> Prenota una consulenza</GoldButton></div>
            </div>
          ) : (
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((c) => (
                <div key={c.id} data-testid={`course-card-${c.id}`} className="bg-white border border-gray-100 shadow-crisp hover:shadow-crisp-lg hover:border-gold-500 transition-all duration-300 flex flex-col">
                  {c.immagine ? <div className="h-44 overflow-hidden"><img src={c.immagine} alt={c.titolo} className="w-full h-full object-cover" /></div> : <div className="h-44 bg-navy-900 flex items-center justify-center"><GraduationCap className="w-12 h-12 text-gold-500" /></div>}
                  <div className="p-6 flex flex-col flex-1">
                    <span className="self-start bg-gold-500 text-navy-900 font-bold uppercase tracking-[0.1em] px-2.5 py-1 text-xs">{c.categoria}</span>
                    <h3 className="mt-3 font-serif text-xl font-semibold text-navy-900">{c.titolo}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{c.descrizione}</p>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
                      {c.durata && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{c.durata}</span>}
                      {c.cfu && <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" />{c.cfu} CFU</span>}
                      {c.modalita && <span>{c.modalita}</span>}
                    </div>
                    <div className="mt-5 flex flex-col gap-2">
                      <button onClick={() => openInfo(c)} data-testid={`course-info-btn-${c.id}`} className="inline-flex items-center justify-center gap-2 border border-navy-900 text-navy-900 font-bold text-xs uppercase tracking-[0.1em] px-4 py-3 hover:bg-navy-900 hover:text-white transition-colors">Richiedi informazioni</button>
                      <Link to={`/prenota?corso=${encodeURIComponent(c.titolo)}`} data-testid={`course-book-btn-${c.id}`} className="inline-flex items-center justify-center gap-2 bg-gold-500 text-navy-900 font-bold text-xs uppercase tracking-[0.1em] px-4 py-3 hover:bg-gold-400 transition-colors"><CalendarCheck className="w-4 h-4" /> Prenota una consulenza</Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info request modal */}
      {modal && (
        <div className="fixed inset-0 z-[60] bg-navy-900/60 flex items-center justify-center p-4" onClick={() => setModal(null)} data-testid="info-modal">
          <div className="bg-white w-full max-w-lg p-8 shadow-crisp-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between"><div><Eyebrow>Richiedi informazioni</Eyebrow><h3 className="mt-2 font-serif text-2xl font-semibold text-navy-900">{modal.titolo}</h3></div><button onClick={() => setModal(null)} aria-label="Chiudi" className="text-slate-400 hover:text-navy-900"><X className="w-6 h-6" /></button></div>
            <form onSubmit={submitInfo} className="mt-6 space-y-4" data-testid="info-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Nome *</Label><Input data-testid="info-nome" value={form.nome} onChange={(e) => set("nome", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                <div><Label>Cognome *</Label><Input data-testid="info-cognome" value={form.cognome} onChange={(e) => set("cognome", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                <div><Label>Telefono *</Label><Input data-testid="info-telefono" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
                <div><Label>Email *</Label><Input type="email" data-testid="info-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 rounded-none border-gray-200" /></div>
              </div>
              <div><Label>Messaggio</Label><Textarea data-testid="info-messaggio" value={form.messaggio} onChange={(e) => set("messaggio", e.target.value)} rows={3} className="mt-2 rounded-none border-gray-200" /></div>
              <GoldButton type="submit" disabled={sending} className="w-full" data-testid="info-submit-btn"><Send className="w-4 h-4" /> {sending ? "Invio…" : "Invia richiesta"}</GoldButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Formazione;
